"use client"

import { useState } from "react"
import { usePathname } from "next/navigation"
import { clx, Drawer } from "@medusajs/ui"
import { XMark, ChevronRightMini, ChevronDownMini, MagnifyingGlass, Envelope } from "@medusajs/icons"
import { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import { StoreFreeShippingPrice } from "@/types/shipping-option/http"
import { useCart } from "@/lib/context/cart-context"
import ShoppingBag from "@/modules/common/icons/shopping-bag"
import User from "@/modules/common/icons/user"
import Menu from "@/modules/common/icons/menu"
import { CartDrawerContent } from "@/modules/cart/components/cart-drawer-content"
import B2BRegistrationNote from "@/modules/common/components/b2b-registration-note"
import {
  compareCategoriesByRank,
  getCategoryRoutePath,
  isCategoryActiveInPathname,
  type NavCategory,
} from "@/lib/util/category-helpers"

// Percent Icon Component
const PercentIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="19" x2="5" y1="5" y2="19" />
    <circle cx="6.5" cy="6.5" r="2.5" />
    <circle cx="17.5" cy="17.5" r="2.5" />
  </svg>
)

// Brand Icon Component
const BrandIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
  </svg>
)

// Mobile Sidebar Drawer Component
export default function MobileSidebar({
  customer,
  freeShippingPrices,
  categories,
  saleLabel,
}: {
  customer: HttpTypes.StoreCustomer | null
  freeShippingPrices: StoreFreeShippingPrice[]
  categories: NavCategory[]
  /** Live sale label ("Akcija −40 %"); the link turns red while it is set. */
  saleLabel?: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [isCartOpen, setIsCartOpen] = useState(false)
  // Track every category id that the user has expanded, so any depth works (main → sub → sub-sub → …).
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<string>>(
    new Set()
  )
  const pathname = usePathname()
  const { cart } = useCart()

  // Calculate cart items
  const items = cart?.items || []
  const totalItems = items?.reduce((acc, item) => acc + item.quantity, 0) || 0

  const mainCategories = categories
    .filter((category) => !category.parent_category_id)
    .sort(compareCategoriesByRank)

  const getSubCategories = (categoryId: string) => {
    return categories
      .filter((category) => category.parent_category_id === categoryId)
      .sort(compareCategoriesByRank)
  }

  // Toggle expansion for a given category id (works at any depth)
  const toggleCategory = (categoryId: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setExpandedCategoryIds((prev) => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }

  const isExpanded = (categoryId: string) => expandedCategoryIds.has(categoryId)
  const closeDrawer = () => setIsOpen(false)

  // Active page checks
  const isAccountPage = pathname.includes("/account")
  const isSearchPage = pathname.includes("/search")
  const isSalePage = pathname.includes("/sale")
  const isBrandsPage = pathname.includes("/brands")
  const isContactPage = pathname.includes("/contact")

  // Handle cart click - close main drawer and open cart drawer
  const handleCartClick = () => {
    setIsOpen(false)
    setTimeout(() => setIsCartOpen(true), 300)
  }

  return (
    <>
      {/* Hamburger Menu Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="small:hidden flex items-center justify-center p-3 text-navy hover:text-gold transition-colors absolute -left-4"
        aria-label="Open menu"
      >
        <Menu className="w-8 h-8" />
      </button>

      {/* Main Sidebar Drawer */}
      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <Drawer.Content
          className="!w-screen !max-w-none z-[70] rounded-none m-0 p-0 !inset-y-0 !right-0 !left-auto h-full min-h-screen flex flex-col"
          overlayProps={{ className: "z-[70]" }}
        >
          {/* Visually hidden title for accessibility */}
          <Drawer.Title className="sr-only">Navigation Menu</Drawer.Title>

          {/* Header with Close (left) and Cart (right) - Enhanced with gradient */}
          <div className="flex items-center justify-between border-b border-ui-border-base px-6 py-5 flex-shrink-0 bg-gradient-to-r from-navy/5 to-gold/5 shadow-sm">
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-white/80 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
              aria-label="Close menu"
            >
              <XMark className="w-5 h-5 text-navy" />
            </button>
            <button
              onClick={handleCartClick}
              className="p-2 hover:bg-white/80 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 relative flex-shrink-0"
              aria-label="Open cart"
            >
              <ShoppingBag className="w-5 h-5 text-navy" />
              {totalItems > 0 && (
                <div className="absolute -top-1 -right-1 bg-gold text-navy text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold shadow-md animate-pulse">
                  {totalItems}
                </div>
              )}
            </button>
          </div>

          <div className="flex flex-col h-full overflow-y-auto">
            {/* Categories — recursive tree, supports unlimited nesting depth */}
            <nav className="bg-gradient-to-b from-white to-gray-50/30">
              <ul className="flex flex-col pt-6 pb-4 px-3 gap-2">
                {mainCategories.map((category) => (
                  <CategoryTreeNode
                    key={category.id}
                    category={category}
                    depth={0}
                    pathname={pathname}
                    allCategories={categories}
                    getSubCategories={getSubCategories}
                    isExpanded={isExpanded}
                    toggleCategory={toggleCategory}
                    closeDrawer={closeDrawer}
                  />
                ))}
              </ul>
            </nav>

            {/* Divider with gradient */}
            <div className="h-2 bg-gradient-to-b from-gray-100/50 to-transparent"></div>

            {/* Search, Brands, B2B Link and Account */}
            <div className="flex flex-col bg-gradient-to-t from-white to-gray-50/50 shadow-inner pt-3 pb-2">
              {/* Search Link */}
              <LocalizedClientLink
                href="/search"
                onClick={() => setIsOpen(false)}
                className={clx(
                  "flex items-center mx-3 mb-2 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm shadow-sm hover:shadow-md active:scale-[0.98] font-serif",
                  isSearchPage
                    ? "bg-navy/10 text-navy font-medium border border-navy/20"
                    : "bg-white hover:bg-navy/5 text-gray-700 hover:text-navy border border-gray-200"
                )}
              >
                <MagnifyingGlass className={clx("w-4 h-4 mr-3 transition-colors", isSearchPage ? "text-navy" : "text-gray-500")} />
                <span className="font-semibold">Išči</span>
              </LocalizedClientLink>

              {/* Sale Link */}
              <LocalizedClientLink
                href="/sale"
                onClick={() => setIsOpen(false)}
                className={clx(
                  "flex items-center mx-3 mb-2 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm shadow-sm hover:shadow-md active:scale-[0.98] font-serif",
                  saleLabel
                    ? "bg-red-600 hover:bg-red-700 text-white border border-red-600"
                    : isSalePage
                      ? "bg-navy/10 text-navy font-medium border border-navy/20"
                      : "bg-white hover:bg-navy/5 text-gray-700 hover:text-navy border border-gray-200"
                )}
              >
                <PercentIcon className={clx("w-4 h-4 mr-3 transition-colors", saleLabel ? "text-white" : isSalePage ? "text-navy" : "text-gray-500")} />
                <span className="font-semibold">{saleLabel ?? "AKCIJA %"}</span>
              </LocalizedClientLink>

              {/* Brands Link */}
              <LocalizedClientLink
                href="/brands"
                onClick={() => setIsOpen(false)}
                className={clx(
                  "flex items-center mx-3 mb-3 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm shadow-sm hover:shadow-md active:scale-[0.98] font-serif",
                  isBrandsPage
                    ? "bg-navy/10 text-navy font-medium border border-navy/20"
                    : "bg-white hover:bg-navy/5 text-gray-700 hover:text-navy border border-gray-200"
                )}
              >
                <BrandIcon className={clx("w-4 h-4 mr-3 transition-colors", isBrandsPage ? "text-navy" : "text-gray-500")} />
                <span className="font-semibold">Blagovne znamke</span>
              </LocalizedClientLink>

              {/* Contact Link */}
              <LocalizedClientLink
                href="/contact"
                onClick={() => setIsOpen(false)}
                className={clx(
                  "flex items-center mx-3 mb-3 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm shadow-sm hover:shadow-md active:scale-[0.98] font-serif",
                  isContactPage
                    ? "bg-navy/10 text-navy font-medium border border-navy/20"
                    : "bg-white hover:bg-navy/5 text-gray-700 hover:text-navy border border-gray-200"
                )}
              >
                <Envelope className={clx("w-4 h-4 mr-3 transition-colors", isContactPage ? "text-navy" : "text-gray-500")} />
                <span className="font-semibold">Kontakt</span>
              </LocalizedClientLink>

              <div className="mx-3 mb-3" onClick={() => setIsOpen(false)}>
                <B2BRegistrationNote />
              </div>
              <LocalizedClientLink
                href="/account"
                onClick={() => setIsOpen(false)}
                className={clx(
                  "flex items-center gap-x-3 mx-3 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm shadow-sm hover:shadow-md active:scale-[0.98] mb-2 font-serif",
                  isAccountPage
                    ? "bg-navy/10 text-navy font-medium border border-navy/20"
                    : "bg-white hover:bg-navy/5 text-gray-700 hover:text-navy border border-gray-200"
                )}
              >
                <User className={clx("w-4 h-4 flex-shrink-0 transition-colors", isAccountPage ? "text-navy" : "text-gray-500")} />
                <span className="font-semibold">
                  {customer ? "Račun" : "Prijava"}
                </span>
              </LocalizedClientLink>
            </div>
          </div>
          </Drawer.Content>
        </Drawer>

      {/* Cart Drawer */}
      <Drawer open={isCartOpen} onOpenChange={setIsCartOpen}>
        <Drawer.Content
          className="z-[70] rounded-none m-0 p-0 inset-y-0 right-0 w-full sm:max-w-md"
          overlayProps={{ className: "z-[70]" }}
        >
          <CartDrawerContent
            customer={customer}
            freeShippingPrices={freeShippingPrices}
          />
        </Drawer.Content>
      </Drawer>
    </>
  )
}

// --- Recursive category tree node ---
// Renders a category row with an expand toggle (when it has children) and,
// when expanded, the same structure for every nested level.
// Depth 0 = main categories (large serif label, white card).
// Depth ≥ 1 = indented rows inside the parent's expanded panel.
type CategoryTreeNodeProps = {
  category: NavCategory
  depth: number
  pathname: string
  allCategories: NavCategory[]
  getSubCategories: (id: string) => NavCategory[]
  isExpanded: (id: string) => boolean
  toggleCategory: (id: string, e: React.MouseEvent) => void
  closeDrawer: () => void
}

function CategoryTreeNode({
  category,
  depth,
  pathname,
  allCategories,
  getSubCategories,
  isExpanded,
  toggleCategory,
  closeDrawer,
}: CategoryTreeNodeProps) {
  const subCategories = getSubCategories(category.id)
  const hasSubCategories = subCategories.length > 0
  const expanded = isExpanded(category.id)
  const isActive = isCategoryActiveInPathname(pathname, category, allCategories)
  const categoryHref = `/categories/${getCategoryRoutePath(category, allCategories)}`

  if (depth === 0) {
    // Top-level main category: keep original styling unchanged.
    return (
      <li className="group">
        <div className="flex items-center rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
          <LocalizedClientLink
            href={categoryHref}
            onClick={closeDrawer}
            className={clx(
              "flex-1 flex items-center px-5 py-4 transition-all duration-300 relative overflow-hidden",
              "before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:to-transparent before:opacity-0 before:transition-opacity before:duration-300",
              isActive
                ? "bg-gradient-to-r from-navy/10 to-gold/10 text-navy font-semibold shadow-inner"
                : "bg-white hover:bg-gradient-to-r hover:from-navy/5 hover:to-gold/5 text-ui-fg-base hover:before:opacity-100 active:scale-[0.98]"
            )}
          >
            <span
              className={clx(
                "font-serif text-lg tracking-wide transition-all duration-300",
                isActive
                  ? "text-navy font-bold"
                  : "text-gray-700 group-hover:text-navy font-semibold"
              )}
            >
              {category.name}
            </span>
          </LocalizedClientLink>
          {hasSubCategories && (
            <button
              onClick={(e) => toggleCategory(category.id, e)}
              className={clx(
                "flex-shrink-0 px-4 py-4 flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 focus:outline-none focus:ring-0",
                isActive
                  ? "bg-gradient-to-r from-navy/10 to-gold/10"
                  : "bg-white hover:bg-gradient-to-r hover:from-navy/5 hover:to-gold/5"
              )}
              aria-label={expanded ? "Collapse subcategories" : "Expand subcategories"}
              aria-expanded={expanded}
            >
              {expanded ? (
                <ChevronDownMini className="w-5 h-5 text-gold" />
              ) : (
                <ChevronRightMini className="w-5 h-5 text-navy/60" />
              )}
            </button>
          )}
        </div>
        {expanded && hasSubCategories && (
          <ul className="mt-2 ml-3 mr-3 bg-white/60 backdrop-blur-sm rounded-lg shadow-inner overflow-hidden border border-gray-100">
            {subCategories.map((child) => (
              <CategoryTreeNode
                key={child.id}
                category={child}
                depth={depth + 1}
                pathname={pathname}
                allCategories={allCategories}
                getSubCategories={getSubCategories}
                isExpanded={isExpanded}
                toggleCategory={toggleCategory}
                closeDrawer={closeDrawer}
              />
            ))}
          </ul>
        )}
      </li>
    )
  }

  // Nested level (sub, sub-sub, …). Indent grows with depth; Tailwind needs
  // literal class names for the JIT, so we pick from a small lookup.
  const indentByDepth: Record<number, string> = {
    1: "pl-10",
    2: "pl-14",
    3: "pl-[4.5rem]",
    4: "pl-[5.5rem]",
  }
  const indentClass = indentByDepth[depth] ?? "pl-[6.5rem]"

  return (
    <li>
      <div
        className={clx(
          "flex items-center transition-all duration-200",
          isActive
            ? "bg-gold/20 text-navy font-medium border-l-4 border-gold"
            : "hover:bg-navy/5 text-gray-600 hover:text-navy border-l-4 border-transparent"
        )}
      >
        <LocalizedClientLink
          href={categoryHref}
          onClick={closeDrawer}
          className={clx(
            "flex-1 flex items-center px-5 py-2.5 font-serif active:scale-[0.98]",
            indentClass
          )}
        >
          <span className="text-sm font-medium">{category.name}</span>
        </LocalizedClientLink>
        {hasSubCategories && (
          <button
            onClick={(e) => toggleCategory(category.id, e)}
            className="flex-shrink-0 px-4 py-2.5 flex items-center justify-center hover:bg-navy/5 focus:outline-none"
            aria-label={expanded ? "Collapse subcategories" : "Expand subcategories"}
            aria-expanded={expanded}
          >
            {expanded ? (
              <ChevronDownMini className="w-4 h-4 text-gold" />
            ) : (
              <ChevronRightMini className="w-4 h-4 text-navy/40" />
            )}
          </button>
        )}
      </div>
      {expanded && hasSubCategories && (
        <ul className="bg-white/40">
          {subCategories.map((child) => (
            <CategoryTreeNode
              key={child.id}
              category={child}
              depth={depth + 1}
              pathname={pathname}
              allCategories={allCategories}
              getSubCategories={getSubCategories}
              isExpanded={isExpanded}
              toggleCategory={toggleCategory}
              closeDrawer={closeDrawer}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

